/**
 * Level loading.
 *
 * There is no index fetch here, and that is not an omission. `GameModule.levels`
 * is the index, bundled inside the game chunk, so it arrives with the module and
 * `DATA-030` and `DATA-035` are satisfied by the dynamic `import()` rather than
 * by anything this file does. What remains is one job: turn an index entry into
 * a validated `Level`, or explain why not.
 *
 * The explanation is the point. `DATA-047` forbids reporting a level as
 * unloadable without saying whether the cause was the network or the data,
 * because the two lead somewhere different — "you are offline and this level
 * has not been downloaded" is a thing a player can act on, "could not load
 * level" is not. So every failure here carries a `cause`, and the shell is
 * expected to switch on it rather than print `detail`.
 *
 * Nothing is cached. `DATA-034` allows one level in memory at a time and the
 * caller holds it; a cache here would quietly hold every level the player has
 * opened this session, which is the same bytes as precaching the game, arrived
 * at by accident.
 */

import type { GameModule } from '@contracts/game-module';
import type { Level } from '@contracts/level';
import {
  checkSchemaVersion,
  describeFaults,
  validateLevel,
  validateLevelIndex,
} from '@contracts/level-guards';
import type { LevelIndex } from '@contracts/level';

/**
 * Why a level could not be loaded, in the four kinds a player can be told apart.
 *
 * `network` and `missing` are both "the bytes did not arrive", but they lead to
 * different advice: one is a connection, the other is a build that shipped an
 * index naming a file it does not contain. `app-outdated` is the only cause with
 * a useful action attached, which is why it is not folded into `data`.
 */
export type LoadCause = 'network' | 'missing' | 'data' | 'app-outdated';

export interface LoadFailure {
  cause: LoadCause;
  /** For logs and bug reports, not for players. `DATA-049`. */
  detail: string;
}

export type Loaded<T> = { ok: true; value: T } | { ok: false; failure: LoadFailure };

const fail = (cause: LoadCause, detail: string): Loaded<never> => ({
  ok: false,
  failure: { cause, detail },
});

/**
 * `DATA-037`. Called once when a game module is registered, not per navigation:
 * the index ships inside the chunk, so it cannot change between two reads, and
 * re-validating it on every visit to level select would be work whose result is
 * known.
 */
export function validateGameIndex(game: GameModule): Loaded<LevelIndex> {
  const result = validateLevelIndex(game.levels);
  if (!result.ok) {
    return fail('data', `game ${game.id} index: ${describeFaults(result.faults)}`);
  }
  if (result.value.gameId !== game.id) {
    return fail('data', `game ${game.id} ships an index claiming gameId ${result.value.gameId}`);
  }
  return { ok: true, value: result.value };
}

/**
 * `DATA-031` and `DATA-032`: fetch one level, validate it, hand it over or
 * refuse it. Called when a level is about to be played and at no other time.
 *
 * `fetchImpl` is injectable because every interesting path through this function
 * is a failure path, and a test that cannot produce a 404 or a truncated body
 * verifies only the case that was going to work anyway.
 */
export async function loadLevel(
  game: GameModule,
  file: string,
  fetchImpl: typeof fetch = fetch,
): Promise<Loaded<Level>> {
  const url = game.levelUrl(file);
  if (url === undefined) {
    // `DATA-046`. The index named a file the game does not ship. Level select
    // stays usable; this one tile does not.
    return fail('missing', `game ${game.id} has no level file named ${file}`);
  }

  let response: Response;
  try {
    response = await fetchImpl(url);
  } catch (error) {
    return fail('network', `fetching ${url}: ${messageOf(error)}`);
  }

  if (!response.ok) {
    // A 404 is a build that shipped an index out of step with its files. Any
    // other status is the network or the server, and retrying may work.
    const cause: LoadCause = response.status === 404 ? 'missing' : 'network';
    return fail(cause, `fetching ${url}: HTTP ${String(response.status)}`);
  }

  let parsed: unknown;
  try {
    parsed = await response.json();
  } catch (error) {
    // `DATA-041`. A truncated or corrupted body is unloadable, full stop. There
    // is no partial interpretation to attempt, and attempting one is how a
    // corrupt file becomes a board with three missing rows.
    return fail('data', `parsing ${url}: ${messageOf(error)}`);
  }

  const version = isRecord(parsed) ? parsed.schemaVersion : undefined;
  switch (checkSchemaVersion(version)) {
    case 'too-new':
      // `DATA-042`. Software must not interpret a document written by a newer
      // version of itself. The player has a stale cached build; say so.
      return fail('app-outdated', `${url} needs a newer version of the application`);
    case 'too-old':
      // `DATA-043`. TAD-001 resolved this against migration.
      return fail('data', `${url} was written for an older schema and is not readable`);
    case 'unreadable':
      return fail('data', `${url} declares no usable schema version`);
    case 'ok':
      break;
  }

  const result = validateLevel(parsed);
  if (!result.ok) {
    // `DATA-044`. Refuse rather than pass partial data to a game.
    return fail('data', `${url}: ${describeFaults(result.faults)}`);
  }

  if (result.value.gameId !== game.id) {
    return fail('data', `${url} belongs to game ${result.value.gameId}, not ${game.id}`);
  }

  return { ok: true, value: result.value };
}

/**
 * `DATA-036`. Warm the HTTP cache for the level the player is most likely to
 * open next, and keep nothing.
 *
 * Deliberately not `loadLevel` with the result stored: holding a parsed level
 * that may never be played would put two levels in memory and break `DATA-034`
 * for a saving measured in milliseconds. Failures are swallowed because a
 * prefetch that fails costs nothing — the real load will fail again, in front of
 * a player, with a message.
 */
export function prefetchLevel(
  game: GameModule,
  file: string,
  fetchImpl: typeof fetch = fetch,
): void {
  const url = game.levelUrl(file);
  if (url === undefined) return;
  void fetchImpl(url).catch(() => undefined);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
